<template>
  <HeadlessListbox as="div" multiple>
    <HeadlessListboxLabel
      v-if="label"
      class="mb-1 block text-left text-xs font-medium text-background-fg/70"
    >
      {{ label }}
    </HeadlessListboxLabel>
    <div class="relative">
      <HeadlessListboxButton
        ref="listboxButtonRef"
        :class="[
          'flex w-full min-w-[300px] flex-nowrap items-center justify-start rounded-md border px-3 py-2 text-left text-sm font-semibold focus:outline-none sm:text-sm',
          hasError
            ? 'border-error text-error placeholder:text-error focus:border-error focus:ring-error'
            : 'focus:border-primary focus:ring-primary',
        ]"
      >
        <span class="mr-2 line-clamp-1">
          {{ selectedLabel }}
        </span>
        <IconChevronDown
          class="ml-auto rounded-full text-background-fg/40"
        />
      </HeadlessListboxButton>
      <template v-if="hasError">
        <p class="mt-1 text-xs font-medium text-error">
          {{ errorMessage }}
        </p>
      </template>
      <Teleport to="#listbox-options">
        <Transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <HeadlessListboxOptions
            ref="listboxOptionsRef"
            class="absolute z-10 mb-6 max-h-60 w-full overflow-y-auto rounded-lg bg-background shadow"
          >
            <HeadlessListboxOption
              v-for="option in computedOptions"
              :key="option.value ?? undefined"
              as="template"
            >
              <li>
                <div
                  :class="[
                    selectedValues.includes(option.value)
                      ? 'bg-background-fg/5'
                      : 'text-background-fg/50 hover:bg-background-fg/1',
                    'flex cursor-pointer items-center justify-start px-3 py-2',
                  ]"
                  @click="toggleOption(option.value)"
                >
                  <component
                    :is="option.icon"
                    v-if="option.icon"
                    class="mr-2"
                  />
                  <span
                    class="line-clamp-1 text-left text-sm font-semibold"
                  >
                    {{ option.label }}
                  </span>
                  <span
                    v-if="
                      selectedValues.includes(option.value)
                    "
                    class="ml-auto"
                    >✔️</span
                  >
                </div>
              </li>
            </HeadlessListboxOption>
          </HeadlessListboxOptions>
        </Transition>
      </Teleport>
    </div>
  </HeadlessListbox>
</template>

<script setup lang="ts">
import { useField } from "vee-validate"
import { createPopper, type Modifier } from "@popperjs/core"
import type { Component } from "vue"

export type SelectOption<
  TValue extends string | number | null =
    | string
    | number
    | null,
> = {
  value: TValue
  label: string
  icon?: Component
}

const props = defineProps<{
  name: string
  options: Array<SelectOption>
  label?: string
  disableNull?: boolean
}>()

const {
  value: inputValue,
  errorMessage,
  setValue,
} = useField<(typeof props)["options"][number]["value"][]>(
  props.name,
  [],
  {
    initialValue: [],
  }
)

const hasError = computed(() => !!errorMessage.value)

const selectedValues = computed(
  () => inputValue.value || []
)

const toggleOption = (
  value: (typeof props)["options"][number]["value"]
) => {
  const index = selectedValues.value.indexOf(value)
  if (index > -1) {
    // Remove option if it's already selected
    setValue(
      selectedValues.value.filter((v) => v !== value)
    )
  } else {
    // Add option if it's not selected
    setValue([...selectedValues.value, value])
  }
}

const selectedLabel = computed(() =>
  selectedValues.value.length
    ? selectedValues.value
        .map((value) => {
          const option = computedOptions.value.find(
            (opt) => opt.value === value
          )
          return option ? option.label : ""
        })
        .join(", ")
    : "Select options"
)

const computedOptions = computed(() => [
  ...(props.disableNull
    ? []
    : [{ value: null, label: "---" }]),
  ...props.options,
])

const listboxOptionsRef = ref()
const listboxButtonRef = ref()

const sameWidthModifier: Modifier<"sameWidth", object> = {
  name: "sameWidth",
  enabled: true,
  phase: "beforeWrite",
  requires: ["computeStyles"],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`
  },
  effect: ({ state }) => {
    const width =
      "offsetWidth" in state.elements.reference
        ? state.elements.reference.offsetWidth
        : 0
    state.elements.popper.style.width = `${width}px`
  },
}

whenever(
  () =>
    listboxButtonRef.value?.el &&
    listboxOptionsRef.value?.el,
  () => {
    createPopper(
      listboxButtonRef.value?.el,
      listboxOptionsRef.value?.el,
      {
        placement: "bottom-start",
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, 4],
            },
          },
          sameWidthModifier,
        ],
      }
    )
  }
)
</script>
